import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/company/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { useEmployeeDetailStats } from '@/hooks/useEmployeeDetailStats';
import { EmployeeProfileBanner } from '@/components/employee/EmployeeProfileBanner';
import { EmployeeStatCards } from '@/components/employee/EmployeeStatCards';
import { EmployeePolicyTable } from '@/components/employee/EmployeePolicyTable';
import { EmployeeDocumentsSection } from '@/components/company/EmployeeDocumentsSection';
import { DependantsSection } from '@/components/insurance/DependantsSection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle } from 'lucide-react';

export function EmployeeDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canEditEmployees } = useCompanyRole();

  const employeeIdTyped = id as Id<'companyEmployees'>; // AS_JUSTIFIED: one-time Id cast from route param at component top

  const employee = useQuery(
    api.companies.employees.get,
    id ? { employeeId: employeeIdTyped } : 'skip'
  );

  const policies = useQuery(
    api['insurance/policies'].listByCompanyEmployee,
    id ? { companyEmployeeId: employeeIdTyped } : 'skip'
  );

  const stats = useEmployeeDetailStats(policies, employee);
  const isLoading = employee === undefined;

  const handleBack = useCallback(() => {
    navigate('/company/employees');
  }, [navigate]);

  const handlePolicyClick = useCallback(
    (policyId: Id<'insurancePolicies'>) => {
      navigate(`/company/policies/${policyId}`);
    },
    [navigate]
  );

  if (isLoading) {
    return (
      <div
        className="min-h-full bg-background-secondary p-8 space-y-4"
        role="status"
        aria-label="Loading employee details"
      >
        <Skeleton className="h-20 w-full rounded-xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-full bg-background-secondary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle
            className="h-12 w-12 text-muted-foreground mx-auto mb-4"
            aria-hidden="true"
          />
          <h2 className="text-lg font-semibold text-foreground mb-1">Employee not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This employee may have been removed or you don&apos;t have access.
          </p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Back to Employees
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary p-8 space-y-6">
      <EmployeeProfileBanner
        employee={employee}
        onBack={handleBack}
        onEdit={canEditEmployees ? () => navigate(`/company/employees/${id}/edit`) : undefined}
      />

      <EmployeeStatCards
        activePolicies={stats.activePolicies}
        totalPremiumPerYear={stats.totalPremiumPerYear}
        expiringSoon={stats.expiringSoonCount}
        docsUploaded={stats.documentsUploaded}
        docsTotal={stats.documentsTotal}
      />

      <Tabs defaultValue="policies">
        <TabsList>
          <TabsTrigger value="policies">Policies ({policies?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="dependants">Dependants</TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="mt-4">
          <EmployeePolicyTable
            policies={policies ?? []}
            onPolicyClick={handlePolicyClick}
            onCreatePolicy={
              canEditEmployees
                ? () =>
                    navigate(
                      `/insurance/policies/new?employeeId=${id}&companyId=${employee.companyId}&source=company_employer`
                    )
                : undefined
            }
          />
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <EmployeeDocumentsSection
            identityDocFront={employee.identityDocFront}
            identityDocBack={employee.identityDocBack}
            drivingLicenseFront={employee.drivingLicenseFront}
            drivingLicenseBack={employee.drivingLicenseBack}
            editable={false}
          />
        </TabsContent>

        <TabsContent value="dependants" className="mt-4">
          <DependantsSection
            parentType="employee"
            employeeId={employeeIdTyped}
            orgId={employee.orgId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
